import { Controller } from "@hotwired/stimulus"

const PLACE = "section-nav:place"

// A side menu of a long page's sections (custom edit): each link jumps to
// its section, and the one you're reading is marked.
export default class extends Controller {
  static targets = ["link"]

  connect() {
    const sections = this.linkTargets.map((link) => document.querySelector(link.hash)).filter(Boolean)
    this.observer = new IntersectionObserver((entries) => {
      entries.filter((e) => e.isIntersecting).forEach((e) => this.mark(this.bottom() ? this.last.id : e.target.id))
    }, { rootMargin: "-20% 0px -70% 0px" })
    sections.forEach((section) => this.observer.observe(section))
    // The last section is too short to reach that band, so at the very
    // bottom of the page it's the one marked.
    this.last = sections.at(-1)
    this.bottom = () => this.last && window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4
    this.atBottom = () => { if (this.bottom()) this.mark(this.last.id) }
    window.addEventListener("scroll", this.atBottom, { passive: true })
    this.remember = (event) => this.rememberPlace(event)
    document.addEventListener("turbo:submit-start", this.remember)
    if (!this.backToPlace()) this.openAt()
  }

  disconnect() {
    this.observer?.disconnect()
    window.removeEventListener("scroll", this.atBottom)
    document.removeEventListener("turbo:submit-start", this.remember)
  }

  // The forms saving as they go (a figure's Stores & options: unlink, a
  // listing's option, renaming an option) come back to this page, which
  // Turbo opens at the top: it's opened where you were instead. The page's
  // own form (#figure-form) goes on to the figure's page.
  rememberPlace(event) {
    if (!this.element.contains(event.target) || event.target.closest("#figure-form")) return
    try { sessionStorage.setItem(PLACE, JSON.stringify({ path: window.location.pathname, y: window.scrollY, at: Date.now() })) } catch {}
  }

  backToPlace() {
    let place
    try { place = JSON.parse(sessionStorage.getItem(PLACE)); sessionStorage.removeItem(PLACE) } catch { return false }
    if (!place || place.path !== window.location.pathname || Date.now() - place.at > 20000 || window.location.hash) return false
    requestAnimationFrame(() => window.scrollTo(0, place.y))
    return true
  }

  // ?section=stores opens the page at that section: after a form saves (Turbo
  // drops a redirect's #fragment), or from an old address redirected here,
  // which can't add one without losing its own (#listing_12). A #fragment wins.
  openAt() {
    const id = new URLSearchParams(window.location.search).get("section")
    if (!id || window.location.hash) return
    const section = document.getElementById(id)
    if (section) requestAnimationFrame(() => section.scrollIntoView())
  }

  mark(id) {
    this.linkTargets.forEach((link) => {
      const on = link.hash === `#${id}`
      link.classList.toggle("on", on)
      if (on) link.setAttribute("aria-current", "true")
      else link.removeAttribute("aria-current")
    })
  }
}
